import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { Link } from 'react-router-dom';
import L from 'leaflet';
import axios from 'axios';
import { Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Custom tree icon
const treeIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIzMiIgaGVpZ2h0PSIzMiIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9IiMxQTNDMzQiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIj48cGF0aCBkPSJNMTIgMnYyMCIvPjxwYXRoIGQ9Im0xNy41IDE2LjUtNSAzLTUtM00xNyAxMmwtNSAzLTUtM00xNyA3bC01IDMtNS0zIi8+PC9zdmc+',
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32],
});

const MapView = () => {
  const [trees, setTrees] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTrees = async () => {
      try {
        const response = await axios.get(`${API}/trees`);
        setTrees(response.data);
      } catch (error) {
        console.error('Error fetching trees:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTrees();
  }, []);

  // Calculate center of all trees
  const center = trees.length > 0
    ? [
        trees.reduce((sum, t) => sum + t.location.lat, 0) / trees.length,
        trees.reduce((sum, t) => sum + t.location.lng, 0) / trees.length,
      ]
    : [-25.2744, 133.7751]; // Center of Australia

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-4rem)]" data-testid="map-loading">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-4rem)] relative" data-testid="map-container">
      <MapContainer
        center={center}
        zoom={5}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {trees.map((tree) => (
          <Marker
            key={tree.id}
            position={[tree.location.lat, tree.location.lng]}
            icon={treeIcon}
          >
            <Popup>
              <div className="p-2 min-w-[200px]" data-testid={`map-popup-${tree.id}`}>
                <img
                  src={tree.image_url}
                  alt={tree.name}
                  className="w-full h-32 object-cover rounded mb-2"
                />
                <h3 className="font-heading font-semibold text-primary text-base mb-1">
                  {tree.name}
                </h3>
                <p className="text-xs italic text-muted-foreground mb-2">
                  {tree.scientific_name}
                </p>
                <Link
                  to={`/tree/${tree.id}`}
                  className="inline-block w-full text-center px-3 py-1.5 bg-primary text-white rounded text-xs font-medium hover:bg-primary/90 transition-colors"
                  data-testid={`view-details-${tree.id}`}
                >
                  View Details
                </Link>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
};

export default MapView;